import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X as XIcon } from "lucide-react";
import { useChangelog, setLastSeen } from "../lib/changelog";

/** Glass-style top-of-screen toast that pops when a new changelog
 *  entry is detected. Sticky until the user dismisses (X) or taps
 *  "View all" (which deep-links to Settings -> What's new + marks
 *  all current entries as seen). Sits above the UpdateBanner so
 *  both can stack cleanly when a new deploy AND a new changelog
 *  entry arrive together. */
export function WhatsNewToast() {
  const { unseen } = useChangelog();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);

  // Re-show the toast when a fresh batch arrives even if the user
  // dismissed an older one earlier in the session.
  const newestSha = unseen[0]?.sha ?? null;
  useEffect(() => {
    if (newestSha) setHidden(false);
  }, [newestSha]);

  if (hidden || unseen.length === 0) return null;
  const newest = unseen[0];
  const more = unseen.length - 1;

  const acknowledge = () => {
    if (newest) setLastSeen(newest.sha);
  };
  const viewAll = () => {
    acknowledge();
    setHidden(true);
    navigate("/settings?section=whatsnew");
  };
  const dismiss = () => {
    acknowledge();
    setHidden(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-[58] mx-auto max-w-2xl px-3 pt-2 pointer-events-none"
    >
      <div className="pointer-events-auto rounded-xl border border-violet-300/60 dark:border-violet-500/40 bg-violet-50/85 dark:bg-violet-900/60 backdrop-blur-md shadow-lg shadow-violet-300/30 dark:shadow-black/40 px-3 py-2.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
          <Sparkles className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-violet-900 dark:text-violet-100 truncate">
            What's new
            {more > 0 ? <span className="ml-1 opacity-70 font-medium">+ {more} more</span> : null}
          </div>
          <div
            className="text-[12px] text-violet-800 dark:text-violet-200/90 truncate"
            title={newest.summary}
          >
            {newest.summary}
          </div>
        </div>
        <button
          type="button"
          onClick={viewAll}
          className="flex-shrink-0 inline-flex items-center rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          View all
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss what's new"
          className="flex-shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center text-violet-700 dark:text-violet-200 hover:bg-violet-200/60 dark:hover:bg-violet-700/60 transition-colors"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
